import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Envelope, Trash } from 'react-bootstrap-icons';
import './Node.css';

const SendEmailNode = ({ data, id, outputEdges, onEdgeCountChange }) => {
  // Define edge labels
  const edgeLabels = ['Send', 'Open', 'Click', 'Not Clicked', 'Bounce', 'Unopened'];

  // Define color schemes based on source
  const getColorClasses = (source) => {
    switch (source) {
      case 'seller':
        return {
          container: 'custom-node node-rounded-full node-border-green',
          header: 'node-header node-bg-green-gradient node-border-green-bottom',
          text: 'node-text-green',
          handle: 'node-handle node-handle-green'
        };
      case 'manager':
        return {
          container: 'custom-node node-rounded-full node-border-purple',
          header: 'node-header node-bg-purple-gradient node-border-purple-bottom',
          text: 'node-text-purple',
          handle: 'node-handle node-handle-purple'
        };
      default: // actions
        return {
          container: 'custom-node node-rounded-lg node-border-green-light',
          header: 'node-header node-bg-green-gradient node-border-green-bottom',
          text: 'node-text-green',
          handle: 'node-handle node-handle-green-light'
        };
    }
  };

  const colors = getColorClasses(data.source || 'actions');

  // Use hover state from data prop (passed from parent)
  const isHovered = data.isHovered || false;

  // Calculate arc positions for handles when hovered - matching image exactly
  const getArcPosition = (index, total, radius = 105) => {
    if (!isHovered || total <= 1) {
      // Vertical layout when not hovered - handles on right edge, evenly spaced
      return {
        top: `${(index * 100) / (total - 1)}%`,
        right: '0',
        left: 'auto',
        transform: 'translateY(-50%)',
      };
    }
    
    // Semi-circular arc layout when hovered (from 10 o'clock to 2 o'clock)
    // Match the image: evenly spaced in a smooth arc extending from node's right side
    // Centered vertically with equal spacing from top and bottom
    const angleRange = Math.PI * 0.7; // 70% of 180 degrees for balanced arc
    const angleStep = total > 1 ? angleRange / (total - 1) : 0;
    const startAngle = -angleRange / 2; // Start from top-right (10 o'clock)
    const angle = startAngle + (angleStep * index);
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    
    // Position relative to container center (container is centered at 50% of node)
    // The container has transform: translateY(-50%), so we position from its center
    return {
      left: `calc(100% + ${x}px)`,
      top: `calc(50% + ${y}px)`, // Position relative to container center (50%)
      transform: 'translate(-50%, -50%)',
    };
  };

  return (
    <div 
      className={`${colors.container} node-hover-lift ${isHovered ? 'node-expanded' : ''}`}
    >
      {/* Updated delete button with hover visibility */}
      <div 
        className={`delete-btn ${data.source === 'seller' || data.source === 'manager' ? 'delete-btn.top-right' : 'delete-btn.top-center'} ${isHovered ? 'show' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (data.onDelete) {
            data.onDelete(id);
          }
        }}
      >
        <Trash className='icon-trash'/>
      </div>

      <div className={colors.header}>
        <div className="node-header-content">
          <Envelope className='node-icon' style={{ color: colors.text.includes('green') ? '#15803d' : colors.text.includes('purple') ? '#7c3aed' : '#15803d' }}/>
          {data?.label?.length > 0 ? "" : <span className={`node-title ${colors.text}`}>Send Email</span>}
          {data?.label?.length > 0 && <span className={`node-title ${colors.text}`}>{data.label}</span>}
        </div>
      </div>
      
     

      {(data.source === 'seller' || data.source === 'manager') ? (
        <>
          <Handle
            type="target"
            position={Position.Top}
            className={colors.handle}
          />
          {data.source !== 'manager' ? <Handle
            type="source"
            position={Position.Bottom}
            className={colors.handle}
          /> : null}
        </>
      ) : (
        <>
          <Handle
            type="target"
            position={Position.Left}
            className={colors.handle}
          />
          <div className={`node-output-handles-vertical ${isHovered ? 'handles-arc-layout' : ''}`}>
            {Array.from({ length: outputEdges }).map((_, index) => {
              const position = getArcPosition(index, outputEdges);
              return (
                <div 
                  key={`output-${index}`} 
                  className={`node-relative node-w-full ${isHovered ? 'output-option-container' : ''}`}
                  style={position}
                >
                  {isHovered ? (
                    // Expanded state: Small rounded rectangle with label and handle
                    <div className="output-option-box">
                      <span className="output-option-label">
                        {edgeLabels[index]}
                      </span>
                      <div className="output-option-handle-wrapper">
                        <Handle
                          type="source"
                          position={Position.Right}
                          id={`o-${index}`}
                          className="output-option-handle"
                          style={{
                            position: 'absolute',
                            right: '0',
                            left: 'auto',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    // Collapsed state: Just handle, no label visible
                    <Handle
                      type="source"
                      position={Position.Right}
                      id={`o-${index}`}
                      className={colors.handle}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SendEmailNode; 
